import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { opsApi, apiError, fmtINR, fmtNum, GRAD, FONT_HEAD, STAGES, STAGE_META, ROLE_RANK } from './opsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trophy, Target, Pencil, Wallet } from 'lucide-react';

export default function OpsPipeline({ user }) {
  const [summary, setSummary] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [editingTarget, setEditingTarget] = useState(null);
  const [targetValue, setTargetValue] = useState('');
  const rank = ROLE_RANK[user.role] || 0;

  const load = useCallback(() => {
    opsApi.get('/pipeline/summary').then((r) => setSummary(r.data));
    opsApi.get('/pipeline/leaderboard').then((r) => setLeaderboard(r.data));
    opsApi.get('/commissions', { params: { page_size: 50 } }).then((r) => setCommissions(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveTarget = async (repId) => {
    try {
      await opsApi.put(`/team/${repId}`, { target_monthly: Number(targetValue) });
      toast.success('Target updated');
      setEditingTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const payCommission = async (id) => {
    try {
      await opsApi.post(`/commissions/${id}/mark-paid`);
      toast.success('Commission paid out');
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const maxStage = summary ? Math.max(...STAGES.map((s) => summary.counts[s] || 0), 1) : 1;

  return (
    <div className="space-y-8" data-testid="ops-pipeline-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>Sales Pipeline</h1>
        <p className="text-sm text-slate-500 mt-1">Funnel, rep targets, commissions &amp; zone-wise leaderboards — scoped to {user.scope_path}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-base font-semibold mb-5" style={FONT_HEAD}>Funnel{summary ? ` · ${fmtNum(summary.total)} institutions` : ''}</h2>
          {summary && (
            <div className="space-y-3" data-testid="pipeline-funnel">
              {STAGES.map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <span className="w-24 text-xs font-medium text-slate-500">{STAGE_META[s].label}</span>
                  <div className="flex-1 h-7 bg-slate-50 rounded-lg overflow-hidden">
                    <div className="h-full rounded-lg"
                      style={{
                        width: `${Math.max(((summary.counts[s] || 0) / maxStage) * 100, summary.counts[s] ? 8 : 0)}%`,
                        background: s === 'active' ? '#34A853' : s === 'churned' ? '#ef4444' : GRAD,
                      }} />
                  </div>
                  <span className="w-10 text-right text-sm font-semibold tabular-nums">{summary.counts[s] || 0}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-base font-semibold mb-4 flex items-center gap-2" style={FONT_HEAD}>
            <Wallet className="w-4 h-4 text-[#34A853]" /> Commissions
          </h2>
          {commissions && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl p-4 border border-amber-100" style={{ backgroundColor: 'rgba(217,119,6,0.06)' }}>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Pending</div>
                  <div className="text-xl font-semibold tabular-nums text-amber-600" style={FONT_HEAD} data-testid="commission-pending-total">{fmtINR(commissions.pending_total)}</div>
                </div>
                <div className="rounded-xl p-4 border border-green-100" style={{ backgroundColor: 'rgba(14,94,51,0.06)' }}>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Paid out</div>
                  <div className="text-xl font-semibold tabular-nums text-[#34A853]" style={FONT_HEAD}>{fmtINR(commissions.paid_total)}</div>
                </div>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {commissions.items.length === 0 && <p className="text-sm text-slate-400">No commissions yet.</p>}
                {commissions.items.map((c) => (
                  <div key={c.id} data-testid={`commission-row-${c.id}`} className="flex items-center justify-between gap-3 text-sm border-b border-slate-50 pb-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.institution_name}</div>
                      <div className="text-xs text-slate-400">{rank >= 2 ? `${c.rep_name} · ` : ''}{c.type.replace('_', ' ')}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-semibold tabular-nums">{fmtINR(c.amount)}</span>
                      {c.status === 'paid'
                        ? <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">paid</span>
                        : rank >= 3
                          ? <Button size="sm" data-testid={`pay-commission-${c.id}`} onClick={() => payCommission(c.id)} className="h-6 text-[10px] rounded-md bg-[#34A853] hover:bg-[#2D9249] text-white px-2">Mark paid</Button>
                          : <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700">pending</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Leaderboard with targets */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 text-sm font-semibold" style={FONT_HEAD}>
          <Trophy className="w-4 h-4 text-amber-500" /> Rep leaderboard — targets &amp; attainment
        </div>
        {leaderboard.length === 0 ? <p className="p-6 text-sm text-slate-400">No reps in your scope.</p> : (
          <table className="w-full text-sm" data-testid="leaderboard-table">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
                <th className="px-5 py-3 font-medium">#</th>
                <th className="px-4 py-3 font-medium">Rep</th>
                <th className="px-4 py-3 font-medium text-right">Activations</th>
                <th className="px-4 py-3 font-medium text-right">In pipeline</th>
                <th className="px-4 py-3 font-medium text-right">MTD revenue</th>
                <th className="px-4 py-3 font-medium text-right">Monthly target</th>
                <th className="px-4 py-3 font-medium">Attainment</th>
                <th className="px-4 py-3 font-medium text-right">Commission</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((l, i) => (
                <tr key={l.rep_id} className="border-b border-slate-50">
                  <td className="px-5 py-3.5">
                    <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="font-medium">{l.name}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{l.employee_code} · {l.scope_path}</div>
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums font-semibold">{l.activations}</td>
                  <td className="px-4 py-3.5 text-right tabular-nums">{l.in_pipeline}</td>
                  <td className="px-4 py-3.5 text-right tabular-nums">{fmtINR(l.mtd_revenue)}</td>
                  <td className="px-4 py-3.5 text-right tabular-nums">
                    {editingTarget === l.rep_id ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Input value={targetValue} onChange={(e) => setTargetValue(e.target.value)} type="number" className="h-7 w-28 text-xs" data-testid="target-edit-input" />
                        <Button size="sm" onClick={() => saveTarget(l.rep_id)} className="h-7 text-xs rounded-md bg-[#34A853] text-white px-2" data-testid="target-save-btn">Save</Button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        {fmtINR(l.target_monthly)}
                        {rank >= 2 && (
                          <button data-testid={`edit-target-${l.rep_id}`} onClick={() => { setEditingTarget(l.rep_id); setTargetValue(String(l.target_monthly || 0)); }}
                            className="text-slate-300 hover:text-[#3B82F6]"><Pencil className="w-3 h-3" /></button>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 w-40">
                    {l.attainment_pct != null ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(l.attainment_pct, 100)}%`, background: l.attainment_pct >= 100 ? '#34A853' : GRAD }} />
                        </div>
                        <span className="text-xs tabular-nums text-slate-500">{l.attainment_pct}%</span>
                      </div>
                    ) : <span className="text-xs text-slate-300 flex items-center gap-1"><Target className="w-3 h-3" />no target</span>}
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums">
                    <span className="text-amber-600">{fmtINR(l.commission_pending)}</span>
                    <span className="text-slate-300 mx-1">/</span>
                    <span className="text-[#34A853]">{fmtINR(l.commission_paid)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
